import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getActiveSchoolId } from '../utils/activeSchool';

import { getApiBaseUrl } from '../apiBase';

const API = getApiBaseUrl();

export default function UploadPage() {
    const { t } = useTranslation(['uploadPage', 'common']);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [file, setFile] = useState(null);
    const [sportTypeId, setSportTypeId] = useState('');
    const [sports, setSports] = useState([]);
    const [schoolId, setSchoolId] = useState('');
    const [groupId, setGroupId] = useState('');
    const [eventId, setEventId] = useState('');
    const [groups, setGroups] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        axios.get(`${API}/sport`).then(r => setSports(r.data)).catch(() => { });
        loadLinks();
    }, []);

    async function loadLinks() {
        try {
            const token = localStorage.getItem('token');
            const activeId = getActiveSchoolId();
            const schoolRes = await axios.get(`${API}/school/my`, {
                headers: { Authorization: `Bearer ${token}` },
                params: activeId ? { schoolId: activeId } : {}
            });
            const sid = schoolRes.data.id;
            setSchoolId(String(sid));
            const [groupRes, eventRes] = await Promise.all([
                axios.get(`${API}/school/${sid}/groups`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/event?schoolId=${sid}`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setGroups(groupRes.data || []);
            setEvents(eventRes.data || []);
        } catch { }
    }

    async function handleUpload(e) {
        e.preventDefault();
        if (!file) return alert(t('uploadPage:pickVideoAlert'));
        setLoading(true);
        setProgress(0);

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('file', file);
        formData.append('sportTypeId', sportTypeId);
        if (schoolId) formData.append('schoolId', schoolId);
        if (groupId) formData.append('groupId', groupId);
        if (eventId) formData.append('schoolEventId', eventId);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API}/video/upload`, formData, {
                headers: { Authorization: `Bearer ${token}` },
                onUploadProgress: e => setProgress(Math.round((e.loaded / e.total) * 100))
            });
            window.location.href = `/video/${res.data.id}`;
        } catch {
            alert(t('uploadPage:uploadErrorAlert'));
        }
        setLoading(false);
    }

    const inputStyle = {
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
        outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
    };

    return (
        <OwnerPageLayout
            title={t('uploadPage:title')}
            subtitle={t('uploadPage:subtitle')}
            maxWidth={580}
        >
                <div className="sp-panel" style={{
                    padding: '36px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
                }}>

                    <form onSubmit={handleUpload}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                {t('uploadPage:videoTitle')}
                            </label>
                            <input
                                value={title} onChange={e => setTitle(e.target.value)}
                                placeholder={t('uploadPage:videoTitlePlaceholder')}
                                required style={inputStyle}
                                onFocus={e => e.target.style.borderColor = '#EB3333'}
                                onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                {t('uploadPage:description')}
                            </label>
                            <textarea
                                value={description} onChange={e => setDescription(e.target.value)}
                                placeholder={t('uploadPage:descriptionPlaceholder')}
                                rows={3}
                                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                                onFocus={e => e.target.style.borderColor = '#EB3333'}
                                onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                {t('uploadPage:sportType')}
                            </label>
                            <select
                                value={sportTypeId} onChange={e => setSportTypeId(e.target.value)}
                                required style={{ ...inputStyle, background: 'white' }}
                                onFocus={e => e.target.style.borderColor = '#EB3333'}
                                onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                            >
                                <option value="">{t('uploadPage:selectOption')}</option>
                                {sports.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}
                            </select>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                {t('uploadPage:linkLabel')}
                            </label>
                            <div style={{ display: 'grid', gap: '8px' }}>
                                <input
                                    value={schoolId ? t('uploadPage:schoolSelected', { id: schoolId }) : t('uploadPage:schoolNotSelected')}
                                    disabled style={{ ...inputStyle, background: '#f8fafc' }}
                                />
                                <select value={groupId} onChange={e => setGroupId(e.target.value)} style={{ ...inputStyle, background: 'white' }}>
                                    <option value="">{t('uploadPage:noGroup')}</option>
                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </select>
                                <select value={eventId} onChange={e => setEventId(e.target.value)} style={{ ...inputStyle, background: 'white' }}>
                                    <option value="">{t('uploadPage:noEvent')}</option>
                                    {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                {t('uploadPage:fileLabel')}
                            </label>
                            <div style={{
                                border: `2px dashed ${file ? '#EB3333' : 'var(--sp-gray-200)'}`,
                                borderRadius: '12px', padding: '24px', textAlign: 'center',
                                background: file ? '#f0ebff' : '#fafafa', cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                                onClick={() => document.getElementById('fileInput').click()}
                            >
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                                    {file ? '✅' : '📁'}
                                </div>
                                <div style={{ fontSize: '14px', color: file ? '#EB3333' : '#888', fontWeight: file ? '600' : '400' }}>
                                    {file ? file.name : t('uploadPage:pickFile')}
                                </div>
                                {file && (
                                    <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                        {t('uploadPage:fileSizeMb', { size: (file.size / 1024 / 1024).toFixed(1) })}
                                    </div>
                                )}
                                <input
                                    id="fileInput" type="file" accept="video/*"
                                    onChange={e => setFile(e.target.files[0])}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        {loading && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                                    <span>{t('uploadPage:uploading')}</span>
                                    <span>{progress}%</span>
                                </div>
                                <div style={{ height: '6px', background: 'var(--sp-gray-200)', borderRadius: '3px' }}>
                                    <div style={{
                                        height: '100%', borderRadius: '3px',
                                        background: '#EB3333', width: `${progress}%`,
                                        transition: 'width 0.3s'
                                    }} />
                                </div>
                            </div>
                        )}

                        <button
                            type="submit" disabled={loading}
                            style={{
                                width: '100%', padding: '14px',
                                background: loading ? '#94a3b8' : '#1A1A1A',
                                color: 'white', border: 'none', borderRadius: '10px',
                                fontSize: '15px', fontWeight: '700',
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {loading ? t('uploadPage:uploadProgress', { progress }) : t('uploadPage:submit')}
                        </button>
                    </form>
                </div>
        </OwnerPageLayout>
    );
}
